import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export type CapacityBadgeSize = 'sm' | 'md';

/**
 * Derived capacity state. NOT a prop — computed from `filled`/`total`.
 * Kept local; @bravemove/types is NOT a dep of ui-web.
 */
export type CapacityState = 'open' | 'nearly' | 'full' | 'sold_out';

export interface CapacityBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Spots already taken. */
  readonly filled: number;
  /** Total capacity. */
  readonly total: number;
  readonly size?: CapacityBadgeSize;
}

/**
 * Resolve the capacity state from the fill ratio.
 *   filled >= total → sold_out
 *   ratio   >= 0.9  → full
 *   ratio   >= 0.7  → nearly
 *   else            → open
 */
function resolveState(filled: number, total: number): CapacityState {
  if (total <= 0 || filled >= total) return 'sold_out';
  const ratio = filled / total;
  if (ratio >= 0.9) return 'full';
  if (ratio >= 0.7) return 'nearly';
  return 'open';
}

interface StateStyle {
  /** Static per-state Tailwind class string (no string interpolation — purge-safe). */
  readonly box: string;
}

const STATE_STYLE: Record<CapacityState, StateStyle> = {
  open: { box: 'bg-success/15 text-ink border border-success/40' },
  nearly: { box: 'bg-warning/15 text-ink border border-warning/40' },
  full: { box: 'bg-danger/15 text-ink border border-danger/40' },
  sold_out: { box: 'bg-ink-3/15 text-ink border border-ink-3/40' },
};

const SIZE: Record<CapacityBadgeSize, string> = {
  // md per spec: font 10px, padding ~3px 9px
  md: 'text-[10px] px-[9px] py-[3px]',
  // sm per spec: font 9px, padding ~2px 7px
  sm: 'text-[9px] px-[7px] py-[2px]',
};

/**
 * Compose the per-state label string.
 *   open                → "{remaining} SPOTS"
 *   nearly / full       → "{remaining} LEFT"
 *   sold_out            → "SOLD OUT"
 */
function resolveLabel(state: CapacityState, remaining: number): string {
  switch (state) {
    case 'open':
      return `${remaining} SPOTS`;
    case 'nearly':
    case 'full':
      return `${remaining} LEFT`;
    case 'sold_out':
      return 'SOLD OUT';
  }
}

/**
 * F41 — `CapacityBadge`. Scarcity pill for tournament divisions + open games.
 *
 * Derives a 4-state tone from the fill ratio and renders a monospace badge.
 * Spec source: [`docs/design/screens/F41-components.jsx`](../../../../../docs/design/screens/F41-components.jsx)
 * `CapacityBadge` (line 56).
 *
 * @example
 *   <CapacityBadge filled={18} total={32} />          // "14 SPOTS" (success)
 *   <CapacityBadge filled={26} total={32} size="sm" />// "6 LEFT" (warning)
 *   <CapacityBadge filled={32} total={32} />          // "SOLD OUT" (ink-3)
 */
export function CapacityBadge({
  filled,
  total,
  size = 'md',
  className,
  ...rest
}: CapacityBadgeProps) {
  const state = resolveState(filled, total);
  const remaining = Math.max(0, total - filled);
  const label = resolveLabel(state, remaining);
  return (
    <span
      role="img"
      aria-label={
        state === 'sold_out'
          ? 'Sold out'
          : `${remaining} of ${total} spots ${state === 'open' ? 'available' : 'left'}`
      }
      data-testid={`capacity-badge-${state}`}
      className={cn(
        'inline-flex items-center rounded font-mono font-extrabold uppercase tracking-[0.08em] whitespace-nowrap align-middle',
        STATE_STYLE[state].box,
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {label}
    </span>
  );
}
